import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import {
  AppointmentsClient,
  BookAppointmentCommand,
  CalendarSlotDto,
  ClientAutocompleteItemResponse,
  ClientsAutocompleteGroupResponse,
  ClientsClient,
  IntervalDto,
} from '../../../infrastructure/shared/model/fss.api';
import { AlertService } from '../../../infrastructure/shared/services/alert.service';
import { LocationAdminService } from '../../../infrastructure/shared/services/location-admin.service';
import { DropdownComponent, DropdownOptions } from '../../../infrastructure/shared/ui/dropdown/dropdown';
import { CalendarComponent } from '../../../infrastructure/shared/ui/calendar/calendar';
import { ButtonComponent, ButtonSeverity, ButtonSize } from '../../../infrastructure/shared/ui/button/button';
import { FieldComponent } from '../../../infrastructure/shared/ui/field/field';
import { Building05 } from '../../../infrastructure/shared/icons/building-05/building-05';
import { UserCircle } from '../../../infrastructure/shared/icons/user-circle/user-circle';
import { Hourglas03 } from '../../../infrastructure/shared/icons/hourglas-03/hourglas-03';
import { XClose } from '../../../infrastructure/shared/icons/x-close/x-close';
import { LinkExternal02 } from '../../../infrastructure/shared/icons/link-external-02/link-external-02';
import { Search } from '../../../infrastructure/shared/icons/search/search';
import { slotCalendarDays } from '../../../infrastructure/shared/functions/slot-calendar';

interface SelectedClient {
  id: string;
  name: string;
  isCompany: boolean;
}

@Component({
  selector: 'app-appointment-form',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    AutoComplete,
    DropdownComponent,
    CalendarComponent,
    ButtonComponent,
    FieldComponent,
    Building05,
    UserCircle,
    XClose,
    Hourglas03,
    LinkExternal02,
    Search,
  ],
  templateUrl: './appointment-form.html',
  styleUrl: './appointment-form.scss',
})
export class AppointmentForm {
  private readonly _fb = inject(FormBuilder);
  private readonly _router = inject(Router);
  private readonly _location = inject(LocationAdminService);
  private readonly _alert = inject(AlertService);
  private readonly _clients = inject(ClientsClient);
  private readonly _appointments = inject(AppointmentsClient);

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;

  clientModel: ClientAutocompleteItemResponse | null = null;
  clientSuggestions = signal<ClientsAutocompleteGroupResponse[]>([]);

  selectedClient = signal<SelectedClient | null>(null);
  packageHours = signal<number | null>(null);
  durationOptions = signal<DropdownOptions[]>([]);
  calendarSlots = signal<CalendarSlotDto[]>([]);
  availableIntervals = signal<IntervalDto[]>([]);

  private readonly _calendarDays = computed(() => slotCalendarDays(this.calendarSlots()));
  minDate = computed(() => this._calendarDays().minDate);
  maxDate = computed(() => this._calendarDays().maxDate);
  disabledDates = computed(() => this._calendarDays().disabledDates);

  form: FormGroup = this._fb.group({
    duration: new FormControl<number | null>(null, { validators: [Validators.required] }),
    date: new FormControl<Date | null>(null, { validators: [Validators.required] }),
    timeSlot: new FormControl<IntervalDto | null>(null, { validators: [Validators.required] }),
  });

  constructor() {
    this.form.controls['duration']!.valueChanges.subscribe((duration) => {
      if (duration != null && this.selectedClient()) {
        this._loadCalendarSlots(this.selectedClient()!.id, duration);
      }
    });

    this.form.controls['date']!.valueChanges.subscribe((date: Date | null) => {
      this.form.controls['timeSlot']!.setValue(null, { emitEvent: false });
      if (!date) {
        this.availableIntervals.set([]);
        return;
      }
      const key = this._dateKey(date);
      const slot = this.calendarSlots().find((s) => this._dateKey(s.dateTime!) === key);
      this.availableIntervals.set(slot?.intervals ?? []);
    });
  }

  get durationCtrl(): FormControl {
    return this.form.controls['duration'] as FormControl;
  }

  get dateCtrl(): FormControl {
    return this.form.controls['date'] as FormControl;
  }

  async searchClients(event: AutoCompleteCompleteEvent): Promise<void> {
    const res = await firstValueFrom(this._clients.getClientsAutocomplete(event.query));
    this.clientSuggestions.set(res.result ?? []);
  }

  async onClientSelected(event: AutoCompleteSelectEvent): Promise<void> {
    const item = event.value as ClientAutocompleteItemResponse;
    const group = this._findGroupFor(item);
    const client: SelectedClient = {
      id: item.id!,
      name: item.name ?? '',
      isCompany: group?.isCompany ?? false,
    };
    this.selectedClient.set(client);
    await this._loadBookingData(client.id);
  }

  isIntervalSelected(interval: IntervalDto): boolean {
    const selected = this.form.controls['timeSlot']!.value as IntervalDto | null;
    return !!selected && selected.from === interval.from && selected.to === interval.to;
  }

  selectInterval(interval: IntervalDto): void {
    this.form.controls['timeSlot']!.setValue(interval);
  }

  formatTime(value: string | undefined): string {
    if (!value) return '';
    const match = value.match(/(\d{1,2}):(\d{2})/);
    if (!match) return value;
    return `${match[1]!.padStart(2, '0')}:${match[2]}`;
  }

  cancel(): void {
    this._location.routeToAppointments();
  }

  resetClient(): void {
    this.clientModel = null;
    this.selectedClient.set(null);
    this.packageHours.set(null);
    this.durationOptions.set([]);
    this.calendarSlots.set([]);
    this.availableIntervals.set([]);
    this.clientSuggestions.set([]);
    this.form.reset();
  }

  openClientInfo(item: ClientAutocompleteItemResponse, event: MouseEvent): void {
    event.stopPropagation();
    this._location.routeToClientDetails(item.id!);
  }

  async submit(): Promise<void> {
    if (!this.selectedClient() || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const date = this.form.controls['date']!.value as Date;
    const interval = this.form.controls['timeSlot']!.value as IntervalDto;
    const duration = this.form.controls['duration']!.value as number;

    const dateTime = this._combineDateAndTime(date, interval.from!);

    const command = new BookAppointmentCommand({
      userId: this.selectedClient()!.id,
      duration,
      dateTime,
    });

    const res = await firstValueFrom(this._appointments.bookAppointment(command));
    this._alert.addSuccessMsg(res.result!);
    this._location.routeToAppointments();
  }

  private _combineDateAndTime(date: Date, time: string): Date {
    const match = time.match(/(\d{1,2}):(\d{2})/);
    const hours = match ? Number(match[1]) : 0;
    const minutes = match ? Number(match[2]) : 0;
    const result = new Date(date);
    result.setHours(hours, minutes, 0, 0);
    return result;
  }

  private _findGroupFor(item: ClientAutocompleteItemResponse): ClientsAutocompleteGroupResponse | undefined {
    return this.clientSuggestions().find((g) => g.items?.some((i) => i.id === item.id));
  }

  private async _loadBookingData(userId: string): Promise<void> {
    const res = await firstValueFrom(this._appointments.getDataForBooking(userId));
    const data = res.result;
    this.packageHours.set(data?.packageHours ?? 0);

    const options: DropdownOptions[] = (data?.durations ?? []).map((d) => ({
      label: `${d}h`,
      value: d,
    }));
    this.durationOptions.set(options);

    const firstDuration = options[0]?.value ?? null;
    this.durationCtrl.setValue(firstDuration);
  }

  private async _loadCalendarSlots(userId: string, duration: number): Promise<void> {
    this.form.controls['date']!.setValue(null, { emitEvent: false });
    this.form.controls['timeSlot']!.setValue(null, { emitEvent: false });
    this.availableIntervals.set([]);

    const res = await firstValueFrom(this._appointments.getCalendarSlots(userId, duration));
    const slots = res.result?.slots ?? [];
    this.calendarSlots.set(slots);
  }

  private _dateKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }
}
